/**
 * FornadaCustoAdicionalDialog Component - Dialog para adicionar custos avulsos
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FornadaCustoAdicional } from "@/types/fornada";
import { fornadaCustoAdicionalSchema, FornadaCustoAdicionalFormData } from "@/schemas/fornadaSchema";
import { TipoProdutoCombobox } from "@/components/tipo-produto/TipoProdutoCombobox";

interface FornadaCustoAdicionalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: FornadaCustoAdicional | null;
  onSave: (item: FornadaCustoAdicional) => void;
}

export function FornadaCustoAdicionalDialog({
  open,
  onOpenChange,
  item,
  onSave,
}: FornadaCustoAdicionalDialogProps) {
  const [selectedProdutoId, setSelectedProdutoId] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FornadaCustoAdicionalFormData>({
    resolver: zodResolver(fornadaCustoAdicionalSchema),
    defaultValues: {
      quantidade: 1,
      valor_unitario: 0,
      valor_total: 0,
    },
  });

  useEffect(() => {
    if (open) {
      if (item) {
        reset(item);
        setSelectedProdutoId(item.tipo_produto_id || "");
      } else {
        reset({
          tipo_produto_id: null,
          descricao: "",
          quantidade: 1,
          valor_unitario: 0,
          valor_total: 0,
        });
        setSelectedProdutoId("");
      }
    }
  }, [open, item, reset]);

  // Buscar dados do produto quando selecionado
  const handleProdutoSelect = (produto: any) => {
    if (produto) {
      setSelectedProdutoId(produto.id_tipo_produto);
      setValue("tipo_produto_id", produto.id_tipo_produto, { shouldValidate: true, shouldDirty: true });
      setValue("descricao", produto.nome, { shouldValidate: true, shouldDirty: true });
      setValue("valor_unitario", produto.preco_base || 0, { shouldValidate: true, shouldDirty: true });
    } else {
      setSelectedProdutoId("");
      setValue("tipo_produto_id", null, { shouldValidate: true, shouldDirty: true });
    }
  };

  // Calcular valor total
  const quantidade = watch("quantidade") || 0;
  const valorUnitario = watch("valor_unitario") || 0;

  useEffect(() => {
    const valorTotal = quantidade * valorUnitario;
    setValue("valor_total", valorTotal);
  }, [quantidade, valorUnitario, setValue]);

  const handleFormSubmit = handleSubmit((data) => {
    try {
      onSave(data as FornadaCustoAdicional);
      reset();
      setSelectedProdutoId("");
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao salvar custo adicional:", error);
    }
  });

  const valorTotal = watch("valor_total");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {item ? "Editar Item Avulso" : "Adicionar Item Avulso"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit} className="space-y-4">
          <div>
            <Label>Produto (opcional)</Label>
            <TipoProdutoCombobox
              value={selectedProdutoId}
              onSelect={handleProdutoSelect}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Selecione um produto cadastrado ou preencha manualmente
            </p>
          </div>

          <div>
            <Label htmlFor="descricao">Descrição *</Label>
            <Input
              id="descricao"
              {...register("descricao")}
              placeholder="Ex: Embalagem plástica, Papel manteiga, etc."
            />
            {errors.descricao && (
              <p className="text-sm text-destructive mt-1">
                {errors.descricao.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="quantidade">Quantidade *</Label>
              <Input
                id="quantidade"
                type="number"
                step="0.001"
                {...register("quantidade", { valueAsNumber: true })}
              />
              {errors.quantidade && (
                <p className="text-sm text-destructive mt-1">
                  {errors.quantidade.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="valor_unitario">Valor Unitário (R$) *</Label>
              <Input
                id="valor_unitario"
                type="number"
                step="0.01"
                {...register("valor_unitario", { valueAsNumber: true })}
              />
              {errors.valor_unitario && (
                <p className="text-sm text-destructive mt-1">
                  {errors.valor_unitario.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center p-4 bg-muted rounded-md">
            <span className="font-semibold">Valor Total:</span>
            <span className="text-xl font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(valorTotal)}
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {item ? "Salvar" : "Adicionar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
